import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ComparisonRow({ stat, label, data }) {
  if (!data) return null;
  const { current, last_year, diff, is_better } = data;

  const formatVal = (v) => {
    if (v === null || v === undefined) return "-";
    if (typeof v === "number") {
      if (["HR", "RBI"].includes(label)) return Math.round(v);
      if (label === "wRC+") return Math.round(v);
      if (label === "fWAR") return v.toFixed(2);
      if (label.includes("%")) return `${(v * 100).toFixed(1)}%`;
      if (Math.abs(v) < 1 && v !== 0) return v.toFixed(3);
      return !Number.isInteger(v) ? v.toFixed(3) : v;
    }
    return v;
  };

  const diffColor = is_better ? "text-green-400" : diff === 0 ? "text-astros-muted" : "text-red-400";

  return (
    <tr className="border-b border-astros-border/30 hover:bg-astros-border/10">
      <td className="py-2 pr-3 text-astros-muted text-xs uppercase tracking-wider font-medium">{label}</td>
      <td className="py-2 px-3 text-center text-white font-semibold">{formatVal(current)}</td>
      <td className="py-2 px-3 text-center text-astros-muted">{formatVal(last_year)}</td>
      <td className={`py-2 px-3 text-center font-bold ${diffColor}`}>
        <span className="inline-flex items-center gap-1">
          {diff > 0 ? "+" : ""}{formatVal(diff)}
          {is_better ? <TrendingUp size={12} /> : diff === 0 ? <Minus size={12} /> : <TrendingDown size={12} />}
        </span>
      </td>
    </tr>
  );
}

function PlayerYoYCard({ player, isPitcher }) {
  const navigate = useNavigate();
  const c = player.comparisons || {};

  // Count improvements
  const total = Object.keys(c).length;
  const better = Object.values(c).filter(v => v.is_better).length;
  const overallBetter = better > total / 2;

  const statOrder = isPitcher
    ? ["ERA", "FIP", "WHIP", "K/9", "BB/9", "fWAR", "K%", "BB%"]
    : ["AVG", "OBP", "SLG", "OPS", "HR", "RBI", "BB%", "K%", "wRC+", "fWAR", "BABIP"];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-astros-surface border ${overallBetter ? "border-green-500/30" : "border-red-500/30"} hover:border-astros-orange transition-colors`}
    >
      {/* Player header */}
      <div
        className="flex items-center gap-3 p-4 border-b border-astros-border cursor-pointer hover:bg-astros-border/20 transition-colors"
        onClick={() => navigate(`/player/${player.id}`)}
        data-testid={`yoy-player-${player.id}`}
      >
        <img
          src={player.photo_url}
          alt={player.name}
          className="w-12 h-12 object-cover bg-astros-navy"
          onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1770228783704-32823e759c03?w=48&h=48&fit=crop"; }}
        />
        <div className="flex-1">
          <h4 className="font-ibm-plex font-semibold text-white text-sm">{player.name}</h4>
          <div className="flex items-center gap-2 text-xs text-astros-muted">
            <span className="text-astros-orange font-bold">#{player.jersey_number || "00"}</span>
            <span>{player.position}</span>
            <span className="text-astros-muted/60">|</span>
            <span>{player.current_games || 0} G in 2026</span>
          </div>
        </div>
        <div className="text-right">
          <span className={`text-xs font-bold px-2 py-1 ${overallBetter ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}`}>
            {better}/{total} better
          </span>
        </div>
      </div>

      {/* Stat comparison table */}
      <div className="p-4">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-astros-muted text-xs">
              <th className="text-left py-1 pr-3 font-medium">Stat</th>
              <th className="text-center py-1 px-3 font-medium">2026</th>
              <th className="text-center py-1 px-3 font-medium">2025</th>
              <th className="text-center py-1 px-3 font-medium">Diff</th>
            </tr>
          </thead>
          <tbody>
            {statOrder.map(key => c[key] ? <ComparisonRow key={key} label={key} data={c[key]} /> : null)}
          </tbody>
        </table>
      </div>
    </motion.div>
  );
}

export default function YearOverYearTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("hitters");

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/stats/year-over-year`);
        setData(res.data);
      } catch (e) {
        console.error("Error fetching YoY:", e);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-astros-orange border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const players = category === "hitters" ? data?.hitters : data?.pitchers;

  return (
    <div data-testid="yoy-tab">
      <h2 className="font-bebas text-2xl sm:text-3xl tracking-tight text-white mb-2">
        YEAR-OVER-YEAR COMPARISON
      </h2>
      <p className="text-astros-muted text-sm mb-6">
        2026 stats vs 2025 at the same point in the season. <span className="text-green-400">Green = better</span>, <span className="text-red-400">Red = worse</span> than last year.
      </p>

      <div className="flex gap-4 mb-6">
        <button
          onClick={() => setCategory("hitters")}
          className={`px-4 py-2 font-bebas text-lg tracking-wide transition-colors ${
            category === "hitters"
              ? "text-astros-orange border-b-2 border-astros-orange"
              : "text-astros-muted hover:text-white"
          }`}
          data-testid="yoy-hitters-btn"
        >
          HITTERS
        </button>
        <button
          onClick={() => setCategory("pitchers")}
          className={`px-4 py-2 font-bebas text-lg tracking-wide transition-colors ${
            category === "pitchers"
              ? "text-astros-orange border-b-2 border-astros-orange"
              : "text-astros-muted hover:text-white"
          }`}
          data-testid="yoy-pitchers-btn"
        >
          PITCHERS
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {players?.map((player) => (
          <PlayerYoYCard key={player.id} player={player} isPitcher={category === "pitchers"} />
        ))}
      </div>

      {(!players || players.length === 0) && (
        <p className="text-astros-muted text-center py-8">No players with 2025 comparison data available</p>
      )}
    </div>
  );
}
